import { DBContactsLabelsThunks, DBLabelThunks, labelSelectByQueryOnlyMaxMin, contactsLabelsSelectByQuery } from "@common-reducers/DBServiceThunks"
import { createAsyncThunk } from "@reduxjs/toolkit"

import { labelActions } from ".."
import { CommonRootState } from "@common/types/common-root-state-type"
import { subscriptionPopupActions } from "@common-reducers/SubscriptionPopupReducer"
import { ContentMessageServiceActions } from "@extension/context/content/services/contentMessageServiceActions"
import { isDBServiceError } from "@common/utils/feathers-error-utils"


//create new label
export const newLabelThunk = createAsyncThunk<void, undefined, { state: CommonRootState }>
    ('labelSlice/newLabelThunk', async (_, thunkApi) => {
        const dispatch = thunkApi.dispatch
        const state = thunkApi.getState()
        const label = state.LabelReducer.label

        dispatch(labelActions.setLabelNetworkStatus('create-label'))

        const labelMaxOrder =
            labelSelectByQueryOnlyMaxMin(
                state.DBLabelReducer,
                {
                    $or: [{
                        deleted: { $exists: false }
                    }, { deleted: false }],
                }, "order", "max") ?? 0;

        const result = await dispatch(DBLabelThunks.create({
            ...label,
            order: labelMaxOrder + 1,
            type: 'custom'
        })).unwrap();

        // Check if the result is an error object
        if (isDBServiceError(result)) {
            console.error('[labels-thunks] Server validation error:', result);

            // Extract error message from the data property
            const errorMessage = result.data.message || String(result);

            // Check if error is due to CRM limit reached
            if (errorMessage.includes('reached your custom contact labels limit') || errorMessage.includes('labels limit')) {
                dispatch(subscriptionPopupActions.openPopup({
                    feature: 'labels',
                    message: errorMessage,
                    upgradeReason: 'labels_limit'
                }));
            }

            dispatch(labelActions.setLabelNetworkStatus('create-label-error'));
            dispatch(labelActions.toggleNewLabel(false));
            throw new Error(errorMessage);
        }

        // Success - handle success part
        dispatch(labelActions.setLabelNetworkStatus('create-label-success'));
        dispatch(labelActions.toggleNewLabel(false));

    })

//update custom label only
export const saveSelectedChatsToLabelThunk = createAsyncThunk<void, undefined, { state: CommonRootState }>
    ('labelSlice/saveSelectedChatsToLabelThunk', async (_, thunkApi) => {
        const dispatch = thunkApi.dispatch
        const state = thunkApi.getState()
        const label = state.LabelReducer.label
        dispatch(labelActions.setLabelNetworkStatus('update-label'))
        const res = await dispatch(DBLabelThunks.patch({ entity: label }))
        if (res.payload) {
            //handle success part
            dispatch(labelActions.setLabelNetworkStatus('update-label-success'))
            dispatch(labelActions.toggleEditLabel(false))
            dispatch(labelActions.setLabel({}))
        }
    })
//update custom label only
export const updateLabelThunk = createAsyncThunk<void, undefined, { state: CommonRootState }>('labelSlice/updateLabelThunk', async (_, thunkApi) => {
    const dispatch = thunkApi.dispatch
    const state = thunkApi.getState()
    const label = state.LabelReducer.label
    dispatch(labelActions.setLabelNetworkStatus('update-label'))
    const res = await dispatch(DBLabelThunks.patch({ entity: label }))
    if (res.payload) {
        //handle success part
        dispatch(labelActions.setLabelNetworkStatus('update-label-success'))
        dispatch(labelActions.toggleEditLabel(false))
        dispatch(labelActions.setLabel({}))
    }
})

// delete label
export const deleteLabelThunk = createAsyncThunk<void, undefined, { state: CommonRootState }>('labelSlice/deleteLabelThunk', async (_, thunkApi) => {
    const dispatch = thunkApi.dispatch
    const state = thunkApi.getState()
    const label = state.LabelReducer.label
    dispatch(labelActions.setLabelNetworkStatus('delete-label'))
    const res = await dispatch(DBLabelThunks.delete({ entity: label }))
    if (res.payload) {
        //handle success part
        dispatch(labelActions.setLabelNetworkStatus('delete-label-success'))
        dispatch(labelActions.toggleDeleteLabel(false))
        dispatch(labelActions.setLabel({}))
    }
})



export const loadLabelsAndContactLabelsThunk = createAsyncThunk<void, undefined, { state: CommonRootState }>
    ('labelSlice/loadLabelsAndContactLabelsThunk', async (_, thunkApi) => {
        const dispatch = thunkApi.dispatch
        await dispatch(DBLabelThunks.find({ deleted: false, $sort: { order: 1 }, $paginate: false }))
        await dispatch(DBContactsLabelsThunks.find({ deleted: false, $paginate: false }))

        // TODO: remove this after migration and also await - migration is 12.2.2026
        // Migrate @lid to @c.us for contact labels (similar to user messages migration)
        const state = thunkApi.getState();
        const contactLabels = contactsLabelsSelectByQuery(state.DBContactsLabelsReducer, {
            $or: [{ deleted: { $exists: false } }, { deleted: false }],
        });

        // Membership is a set — track the (labelId, waChatId) pairs we already have
        // so re-pointing a @lid row onto its @c.us form can't mint a duplicate. The
        // migration previously patched blind, which is one of the ways a chat ended
        // up with several "inbox" rows and needed one click per row to be removed.
        const seenPairs = new Set(
            contactLabels
                .filter((cl) => !cl?.waChatId?.includes("@lid"))
                .map((cl) => `${cl.labelId}:${cl.waChatId}`)
        );

        for (const contactLabel of contactLabels) {
            if (contactLabel?.waChatId?.includes("@lid")) {
                const convertedId = await ContentMessageServiceActions.convertWaLidSerializedToCusSerialized(contactLabel.waChatId);
                console.log("[labels migration] convertedId", convertedId);
                if (convertedId?.includes("@c.us")) {
                    const pair = `${contactLabel.labelId}:${convertedId}`;
                    if (seenPairs.has(pair)) {
                        // The @c.us row already exists — patching would duplicate it.
                        console.log("[labels migration] dropping redundant @lid row for", pair);
                        dispatch(DBContactsLabelsThunks.delete({ entity: { _id: contactLabel._id } }));
                    } else {
                        seenPairs.add(pair);
                        dispatch(DBContactsLabelsThunks.patch({
                            entity: {
                                _id: contactLabel._id,
                                waChatId: convertedId,
                            }
                        }));
                    }
                }
            }
        }
    })

// Create contact-label with CRM limit error handling
export const createContactLabelThunk = createAsyncThunk<
    any,
    { contactLabel: any },
    { state: CommonRootState }
>(
    'labelSlice/createContactLabelWithLimitCheck',
    async ({ contactLabel }, thunkApi) => {
        const dispatch = thunkApi.dispatch;

        const result = await dispatch(DBContactsLabelsThunks.create(contactLabel)).unwrap();

        // Check if the result is an error object
        if (isDBServiceError(result)) {
            console.error('[labels-thunks] Error assigning label to contact:', result);

            // Extract error message from the data property
            const errorMessage = result.data.message || String(result);

            // Check if error is due to CRM limit reached
            if (errorMessage.includes('contact labels limit') || errorMessage.includes('labels limit')) {
                dispatch(subscriptionPopupActions.openPopup({
                    feature: 'labels',
                    message: errorMessage,
                    upgradeReason: 'contact_labels_limit'
                }));
                throw new Error(errorMessage);
            } else {
                // Generic error - re-throw
                throw new Error(errorMessage);
            }
        }

        // Success - return the created contact-label
        return result;
    }
);





